import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import toast from 'react-hot-toast'
import { ChevronDownIcon } from '@/components/ui/icons'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from '@/components/ui/select'
import { Spinner } from '@/components/ui/spinner'
import { RsvpStatusBadge } from '@/features/marquee/components/rsvp-status-badge'
import { makeQueryKeys } from '@/features/marquee/query-keys'
import { MARQUEE_RSVP_ENTRY_OPTIONS } from '@/features/marquee/types'
import {
	invalidStateMessage,
	rsvpCellToLabelKey
} from '@/features/marquee/utils'
import { errorHandler } from '@/helpers/utils'
import {
	type MarqueeAttendee,
	type MarqueeRsvpStatus,
	setAttendeeRsvp
} from '@/sdk'
import { useTranslation } from '@/translations/i18n'

interface RsvpStatusCellProps {
	attendee: MarqueeAttendee
	eventId?: string
	/**
	 * Whether the invite answer can still be corrected. The write is `pre_work`
	 * only: the lock closes the RSVP window and rewrites the still-`invited` rows
	 * to `no_response`, so afterwards the endpoint answers 409 `invalid-state`
	 * and the cell is the badge alone.
	 */
	editable: boolean
}

/**
 * The roster's invite answer, editable in place.
 *
 * The picker offers the three answers an operator can mean before the lock, the
 * same set the manual-add form uses. `no_response` is not among them even though
 * the endpoint accepts it: it is the verdict the lock writes, and choosing it by
 * hand would close a row's window while the event's is still open. An import can
 * still produce it (the file's vocabulary has "No Response"), so it is added to
 * the list when it is the current value rather than silently dropped.
 *
 * `null` is likewise only a starting state: the endpoint has no way back to it,
 * because an absent answer means the import carried no RSVP fact and only an
 * import can say that.
 */
export function RsvpStatusCell({
	attendee,
	eventId,
	editable
}: RsvpStatusCellProps) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()

	const current = attendee.rsvp_status ?? null
	const options = useMemo<MarqueeRsvpStatus[]>(() => {
		const entries: MarqueeRsvpStatus[] = [...MARQUEE_RSVP_ENTRY_OPTIONS]
		return current && !entries.includes(current)
			? [...entries, current]
			: entries
	}, [current])

	// `funnel_status` derives from this column, so the row's lane moves with it;
	// refetching the roster is what picks both up.
	const save = useMutation({
		mutationFn: async (rsvpStatus: MarqueeRsvpStatus) => {
			const response = await setAttendeeRsvp({
				path: { attendeeId: attendee.id },
				body: { rsvp_status: rsvpStatus }
			})
			return response.data
		},
		onSuccess: async (_data, rsvpStatus) => {
			await queryClient.invalidateQueries({
				queryKey: makeQueryKeys.marquee.events.detail(eventId ?? '')
			})
			// Names the row as well as the new answer: the roster is long and a
			// mis-clicked select would otherwise confirm silently.
			toast.success(
				t('marquee:attendee_rsvp_saved', {
					name: attendee.name,
					status: t(rsvpCellToLabelKey(rsvpStatus))
				})
			)
		},
		onError: (error) => {
			// The cell is only editable in `pre_work`, so a refusal here means the
			// event was locked under the operator — in another tab, or while this
			// roster sat open. The shared `invalid-state` code names no action, so
			// this one says which window closed.
			const refusal = invalidStateMessage({
				error,
				action: 'set_rsvp'
			})
			if (refusal) {
				toast.error(
					t(refusal.key, {
						state: refusal.stateKey ? t(refusal.stateKey) : ''
					})
				)
				return
			}
			errorHandler(error)
		}
	})

	if (!(editable && eventId)) {
		return <RsvpStatusBadge status={current} />
	}

	return (
		<Select
			disabled={save.isPending}
			onValueChange={(next: MarqueeRsvpStatus | null) =>
				next && save.mutate(next)
			}
			value={current}
		>
			{/* The badge *is* the trigger, rather than a badge parked inside
			    one: the default trigger brings its own border, white fill and
			    fixed height, which wrapped the coloured pill in a second box and
			    padded it to a field width, so the editable cell no longer matched
			    the read-only rendering of the same value. `bare` drops that
			    chrome; the pill keeps its own shape and sizes to its own text. */}
			<SelectTrigger
				aria-label={t('marquee:attendee_col_rsvp')}
				bare
				className='rounded-full hover:not-disabled:opacity-80 data-popup-open:opacity-80'
			>
				{/* `flex-1` by default, which would stretch the pill across the
				    cell — the width the bare trigger exists to avoid. */}
				<SelectValue className='flex-none'>
					{(value: MarqueeRsvpStatus | null) => (
						<RsvpStatusBadge status={value}>
							{/* Kept inside the pill so a saving row holds its
							    width and the cells beside it do not shift. */}
							{save.isPending ? (
								<Spinner className='shrink-0' size={12} />
							) : (
								<ChevronDownIcon
									className='shrink-0 in-data-popup-open:rotate-180 opacity-60 transition-transform duration-150'
									size={12}
								/>
							)}
						</RsvpStatusBadge>
					)}
				</SelectValue>
			</SelectTrigger>
			<SelectContent className='min-w-30'>
				{options.map((option) => (
					<SelectItem key={option} value={option}>
						{t(rsvpCellToLabelKey(option))}
					</SelectItem>
				))}
			</SelectContent>
		</Select>
	)
}
